import { useState, useEffect, useCallback, useMemo } from "react";
import CircularProgress from '@mui/material/CircularProgress';
import { FormControl, Select, MenuItem } from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { startOfMonth, format, parse, subYears, subMonths, parseISO, endOfMonth } from 'date-fns';
import '../styles/NPSPage.css';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import NPSChart from './NPSChart';
import ProductSelector from './ProductSelector';
import TimeRangeSelector from './TimeRangeSelector';
import Toggle from './Toggle';
import { NPS_CONFIG, prepareChartData } from '../functions/bayesianNPS';
import { getAssessmentKeywords } from '../utils/assessmentKeywords';
import { apiPost } from '../services/apiClient';
import { useStockSeries } from '../hooks/useStockSeries';
import { useStockPremium } from '../hooks/useStockPremium';
import {
    pricesForMonthLabels, premiumForMonthLabels, benchmarkLabel,
    STOCK_COLOR, STOCK_DASH_ARRAY, PREMIUM_MODES,
} from '../utils/stockOverlay';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Define chart colors for main product and competitors
const CHART_COLORS = {
    main: {
        line: '#0057FF',  // bright blue for main product
        fill: 'rgba(0, 87, 255, 0.1)'
    },
    competitors: [
        { line: '#FF8A00', fill: 'rgba(255, 138, 0, 0.1)' },    // Orange
        { line: '#00A86B', fill: 'rgba(0, 168, 107, 0.1)' },    // Green
        { line: '#7A1FFF', fill: 'rgba(122, 31, 255, 0.1)' },   // Purple
        { line: '#E63946', fill: 'rgba(230, 57, 70, 0.1)' },    // Red
        { line: '#0891B2', fill: 'rgba(8, 145, 178, 0.1)' },    // Cyan
        { line: '#CA8A04', fill: 'rgba(202, 138, 4, 0.1)' },    // Gold
    ]
};

// Metric card component for displaying NPS metrics
const MetricCard = ({ period, metrics, onClick, getNPSColorClass }) => {
    return (
        <div className="metric-card" onClick={onClick}>
            <h3>{period}</h3>
            
            <div className="metric-row">
                <span className="metric-label">NPS (Adjusted)</span>
                <span className={`metric-value ${getNPSColorClass(metrics?.nps || 0)}`}>
                    {metrics?.nps || '-'}
                </span>
            </div>

            <div className="metric-row">
                <span className="metric-label">NPS (Raw)</span>
                <span className={`metric-value ${getNPSColorClass(metrics?.noPaddingNps || 0)}`}>
                    {metrics?.noPaddingNps || '-'}
                </span>
            </div>

            <div className="metric-row">
                <span className="metric-label">Average Rating</span>
                <span className="metric-value rating">
                    {metrics?.raw !== null ? `${(metrics?.raw || 0).toFixed(1)}/10` : '-'}
                </span>
            </div>

            <div className="sample-size">
                Sample size: {metrics?.count || 0}
            </div>
        </div>
    );
};

// Helper function to generate a range of months (copied from bayesianNPS.js for local use)
function generateMonthRange(startMonth, endMonth) {
    if (!startMonth || !endMonth || startMonth > endMonth) {
        console.warn('[NPSPage] Invalid month range provided to generateMonthRange:', { startMonth, endMonth });
        return [];
    }
    try {
        const [startYear, startMo] = startMonth.split('-').map(n => parseInt(n));
        const [endYear, endMo] = endMonth.split('-').map(n => parseInt(n));

        if (isNaN(startYear) || isNaN(startMo) || isNaN(endYear) || isNaN(endMo)) {
             console.error('[NPSPage] Error parsing month range in generateMonthRange:', { startMonth, endMonth });
             return [];
        }
        
        const months = [];
        let currentYear = startYear;
        let currentMonth = startMo;
        
        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMo)) {
            months.push(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
            currentMonth++;
            if (currentMonth > 12) {
                currentMonth = 1;
                currentYear++;
            }
        }
        // console.log('[NPSPage] Generated month range:', months);
        return months;
    } catch (error) {
        console.error("[NPSPage] Error in generateMonthRange:", error, "Input:", { startMonth, endMonth });
        return [];
    }
}

const NPSPage = ({topicMap, companyTicker, onLoadCompetitors,
    // Optional shared product selection: when the parent page passes a
    // topic id that's valid for this component's own data (has NPS
    // results), adopt it; otherwise this component keeps its own
    // priority-1 default. Selections made here are pushed back up via
    // onProductChange so other sections of the page can pick them up.
    selectedProduct: sharedSelectedProduct, onProductChange}) => {
    const [postsReferenced, setPostsReferenced] = useState([]);
    const [showPostDisplay, setShowPostDisplay] = useState(false);
    const [showConfidenceIntervals, setShowConfidenceIntervals] = useState(false);
    // Overlay mode: 'off' | 'price' | 'peers' | 'snp500' | 'sector'.
    const [overlayMode, setOverlayMode] = useState('off');

    const title = "Net Promoter Score"
    const stat_name = "NPS"
    
    const [averages, setAverages] = useState({ 
        year: { nps: null, raw: null, noPaddingNps: null, count: null },
        threeYears: { nps: null, raw: null, noPaddingNps: null, count: null },
        fiveYears: { nps: null, raw: null, noPaddingNps: null, count: null }
    });
    
    const [timeRange, setTimeRange] = useState('3y'); // Default time range
    const [selectedCompetitors, setSelectedCompetitors] = useState(new Set());
    const [competitorData, setCompetitorData] = useState({});
    const [showCompetitors, setShowCompetitors] = useState(false);
    const [competitorsLoaded, setCompetitorsLoaded] = useState(false);
    // Lazy-initialize from the shared selection (if it's one of this
    // company's topics) so local state is never empty on first render —
    // see PricingPlot.js for why an empty initial value lets
    // ProductSelector's own internal auto-select effect win a race
    // against the shared-selection logic below. (Optimistic: doesn't yet
    // know if the topic has NPS results, same as topicMap here initially.)
    const [selectedProduct, setSelectedProduct] = useState(() =>
        (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) ? sharedSelectedProduct : null
    );
    const [selectedTimePeriod, setSelectedTimePeriod] = useState(null);

    // Load competitor data when user clicks "Show Competitors"
    useEffect(() => {
        if (showCompetitors && !competitorsLoaded && onLoadCompetitors) {
            console.log('Loading NPS competitor data...');
            setCompetitorsLoaded(true);
            onLoadCompetitors();
        }
    }, [showCompetitors, competitorsLoaded, onLoadCompetitors]);
    const [npsResults, setNpsResults] = useState(null);
    const [filteredChartData, setFilteredChartData] = useState(null);
    const [selectedProductName, setSelectedProductName] = useState("");

    // Find the priority 1 topic (highest priority = lowest number)
    const priority1Topic = useMemo(() => {
        if (!topicMap) return null;

        const sorted = Object.values(topicMap)
            .sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            });

        return sorted[0] || null;
    }, [topicMap]);

    // Check if priority 1 topic has loaded its NPS data
    const priority1Loaded = useMemo(() => {
        return priority1Topic?.npsResults &&
               Array.isArray(priority1Topic.npsResults) &&
               priority1Topic.npsResults.length > 0;
    }, [priority1Topic]);

    // Filter topicMap to only include topics with NPS data (pre-calculated npsResults)
    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};

        const filtered = {};
        Object.keys(topicMap).forEach(id => {
            const topic = topicMap[id];
            // Check for pre-calculated npsResults from Supabase RPC
            if (topic?.npsResults &&
                Array.isArray(topic.npsResults) &&
                topic.npsResults.length > 0) {
                filtered[id] = topic;
            }
        });

        return filtered;
    }, [topicMap]);

    // Select a default topic on initial load only. Prefers the page-wide
    // shared topic selection (if valid for this component's own filtered
    // data); otherwise falls back to the priority-1 topic. Combined into a
    // single effect so there's no race between the shared-selection and
    // own-default logic.
    const [hasInitialSelection, setHasInitialSelection] = useState(false);

    useEffect(() => {
        if (hasInitialSelection) return;
        if (sharedSelectedProduct && filteredTopicMap[sharedSelectedProduct]) {
            setSelectedProduct(sharedSelectedProduct);
            setHasInitialSelection(true);
            return;
        }
        if (priority1Loaded && priority1Topic) {
            setSelectedProduct(priority1Topic.id);
            setHasInitialSelection(true);
        }
    }, [priority1Loaded, priority1Topic, hasInitialSelection, sharedSelectedProduct, filteredTopicMap]);

    const selectProduct = (productId) => {
        setSelectedProduct(productId);
        if (onProductChange) onProductChange(productId);
    };

    // Helper function to calculate weighted average NPS from monthly results for a time period
    const calculatePeriodAverage = useCallback((results, yearsBack) => {
        if (!results || !Array.isArray(results) || results.length === 0) {
            return { nps: null, raw: null, noPaddingNps: null, count: 0 };
        }

        const now = new Date();
        const cutoffDate = subYears(now, yearsBack);
        const cutoffString = `${cutoffDate.getFullYear()}-${String(cutoffDate.getMonth() + 1).padStart(2, '0')}`;

        // Filter to months within the time period (exclude forward-filled months for accurate counts)
        const periodResults = results.filter(r =>
            r.month >= cutoffString && !r.isForwardFilled
        );

        if (periodResults.length === 0) {
            return { nps: null, raw: null, noPaddingNps: null, count: 0 };
        }

        // Calculate weighted average NPS based on sample size
        let totalWeight = 0;
        let weightedSum = 0;
        let totalCount = 0;

        periodResults.forEach(r => {
            const weight = r.count || 1;
            weightedSum += (r.meanNPS || 0) * weight;
            totalWeight += weight;
            totalCount += r.count || 0;
        });

        const avgNPS = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : null;

        return {
            nps: avgNPS,
            raw: null,  // Not available from pre-calculated data
            noPaddingNps: avgNPS,  // Same as adjusted since already Bayesian
            count: totalCount
        };
    }, []);

    // Process pre-calculated NPS data when topic data and selected product are available
    useEffect(() => {
        if (selectedProduct && topicMap && topicMap[selectedProduct]) {
            const productData = topicMap[selectedProduct];

            // Use pre-calculated npsResults from Supabase RPC
            if (productData.npsResults && Array.isArray(productData.npsResults)) {
                console.log('Using pre-calculated NPS data for:', productData.name, '-', productData.npsResults.length, 'months');

                // Store the pre-calculated results directly
                setNpsResults(productData.npsResults);

                // Calculate period averages from the monthly data
                const yearAverage = calculatePeriodAverage(productData.npsResults, 1);
                const threeYearsAverage = calculatePeriodAverage(productData.npsResults, 3);
                const fiveYearsAverage = calculatePeriodAverage(productData.npsResults, 5);

                setAverages({
                    year: yearAverage,
                    threeYears: threeYearsAverage,
                    fiveYears: fiveYearsAverage
                });
            }
        }
    }, [selectedProduct, topicMap, calculatePeriodAverage]);

    // Filter results based on time range and add competitor data
    useEffect(() => {
        if (!npsResults || !npsResults.length) return;
        
        console.log('[NPSPage] Filtering NPS results based on time range:', timeRange);
        
        // --- Calculate Cutoff Date ---
        const now = new Date();
        let cutoffDate;
        switch(timeRange) {
            case '1y': cutoffDate = subYears(now, 1); break;
            case '2y': cutoffDate = subYears(now, 2); break;
            case '3y': cutoffDate = subYears(now, 3); break;
            case '5y': cutoffDate = subYears(now, 5); break;
            case '10y': cutoffDate = subYears(now, 10); break;
            case 'all': cutoffDate = new Date(0); break; // Beginning of time
            default: cutoffDate = subYears(now, 3); // Default to 3 years
        }
        const cutoffYear = cutoffDate.getFullYear();
        const cutoffMonth = String(cutoffDate.getMonth() + 1).padStart(2, '0');
        const cutoffString = `${cutoffYear}-${cutoffMonth}`;
        console.log('[NPSPage] Cutoff date string:', cutoffString);

        // --- Filter Main Product Data ---
        const filteredMainResults = npsResults.filter(result => result.month >= cutoffString);
        filteredMainResults.sort((a, b) => a.month.localeCompare(b.month));
        console.log(`[NPSPage] Filtered main product results: ${filteredMainResults.length} points`);
        if (filteredMainResults.length > 0) {
            console.log(`[NPSPage] Main product date range: ${filteredMainResults[0].month} to ${filteredMainResults[filteredMainResults.length - 1].month}`);
        } else {
            console.log(`[NPSPage] No main product data within time range.`);
        }

        let allFilteredResults = [...filteredMainResults]; // Collect all results for range calculation
        let competitorResultsList = []; // Store filtered competitor results for dataset creation

        // --- Process and Filter Competitor Data (using pre-calculated npsResults) ---
        if (showCompetitors && selectedProduct && topicMap[selectedProduct]?.competitors) {
            const competitors = topicMap[selectedProduct].competitors;
            console.log(`[NPSPage] Processing ${competitors.length} competitors`);

            competitors.forEach((competitor, index) => {
                // Use pre-calculated npsResults from Supabase RPC
                console.log(`[NPSPage] --> Competitor object:`, { id: competitor.id, topic: competitor.topic, hasNpsResults: !!competitor.npsResults });
                if (competitor.npsResults && Array.isArray(competitor.npsResults) && competitor.npsResults.length > 0) {
                    console.log(`[NPSPage] --> Processing competitor: ${competitor.topic} with id: ${competitor.id} (${competitor.npsResults.length} months)`);

                    // Filter competitor results by time range AND sort
                    const filteredCompetitorResults = competitor.npsResults.filter(result => result.month >= cutoffString);
                    filteredCompetitorResults.sort((a, b) => a.month.localeCompare(b.month));

                    if (filteredCompetitorResults.length > 0) {
                        console.log(`[NPSPage] --> Filtered competitor ${competitor.topic}: ${filteredCompetitorResults.length} points`);
                        console.log(`[NPSPage] --> Competitor ${competitor.topic} date range: ${filteredCompetitorResults[0].month} to ${filteredCompetitorResults[filteredCompetitorResults.length - 1].month}`);
                        allFilteredResults.push(...filteredCompetitorResults); // Add to combined list
                        competitorResultsList.push({ name: competitor.topic, topicId: competitor.id, results: filteredCompetitorResults, index }); // Store for dataset creation with topicId
                    } else {
                        console.log(`[NPSPage] --> No data for competitor ${competitor.topic} within time range.`);
                    }
                } else {
                    console.log(`[NPSPage] --> No NPS results data for competitor: ${competitor.topic}`);
                }
            });
        } else {
            console.log('[NPSPage] No competitors to process or showCompetitors is false.');
        }

        // --- Determine Overall Date Range and Generate Labels ---
        let comprehensiveLabels = [];
        if (allFilteredResults.length > 0) {
             // Log before sorting/finding range
             console.log(`[NPSPage] Calculating overall range from ${allFilteredResults.length} total data points.`);
             const monthsInData = allFilteredResults.map(r => r.month);
             console.log('[NPSPage] All months present in data:', [...new Set(monthsInData)].sort()); // Show unique sorted months

            // Sort all results by month to easily find min/max
            allFilteredResults.sort((a, b) => a.month.localeCompare(b.month));
            const overallMinMonth = allFilteredResults[0].month;
            const overallMaxMonth = allFilteredResults[allFilteredResults.length - 1].month;
            console.log(`[NPSPage] Overall date range determined: ${overallMinMonth} to ${overallMaxMonth}`);
            
            comprehensiveLabels = generateMonthRange(overallMinMonth, overallMaxMonth);
            console.log(`[NPSPage] Generated ${comprehensiveLabels.length} comprehensive labels: [${comprehensiveLabels[0]}, ..., ${comprehensiveLabels[comprehensiveLabels.length-1]}]`);

        } else {
             console.log('[NPSPage] No data found for main product or competitors in the selected range. Chart will be empty or based on fallback.');
             // Fallback: If only main product had original data (before filtering), use its labels?
             // Or better: Use a minimal default range if filter yields nothing? For now, empty is ok.
             comprehensiveLabels = []; 
        }

        // --- Prepare Datasets --- 
        let allDatasets = [];

        // Prepare Main Product Dataset
        if (filteredMainResults.length > 0) {
            const mainDatasetPrepared = prepareChartData(
                filteredMainResults, 
                selectedProductName || 'Product',
                showConfidenceIntervals
            )?.datasets; 

            if (mainDatasetPrepared) {
                mainDatasetPrepared.forEach(dataset => {
                    // Add topicId to dataset for click handling
                    dataset.topicId = selectedProduct;
                    if (dataset.label.includes('Confidence Band')) {
                        dataset.backgroundColor = CHART_COLORS.main.fill;
                        dataset.borderColor = CHART_COLORS.main.line;
                    } else {
                        dataset.borderColor = CHART_COLORS.main.line;
                        dataset.backgroundColor = CHART_COLORS.main.fill;
                    }
                });
                allDatasets.push(...mainDatasetPrepared);
                console.log('[NPSPage] Main product dataset prepared.');
            } else {
                 console.log('[NPSPage] Main product dataset preparation returned null/undefined.');
            }
        } else {
            console.log('[NPSPage] No filtered main product data to prepare dataset.');
        }


        // Prepare Competitor Datasets
        competitorResultsList.forEach(({ name, topicId: compTopicId, results, index }) => {
            console.log(`[NPSPage] Preparing competitor ${name} with topicId: ${compTopicId}`);
            const competitorDatasetPrepared = prepareChartData(
                results,
                name,
                showConfidenceIntervals
            )?.datasets;

             if (competitorDatasetPrepared) {
                const colorSet = CHART_COLORS.competitors[index % CHART_COLORS.competitors.length];
                competitorDatasetPrepared.forEach(dataset => {
                    // Add topicId to dataset for click handling
                    dataset.topicId = compTopicId;
                    console.log(`[NPSPage] Set topicId ${compTopicId} on dataset: ${dataset.label}`);
                    if (dataset.label.includes('Confidence Band')) {
                        dataset.backgroundColor = colorSet.fill;
                        dataset.borderColor = colorSet.line;
                    } else {
                        dataset.borderColor = colorSet.line;
                        dataset.backgroundColor = colorSet.fill;
                    }
                });
                allDatasets.push(...competitorDatasetPrepared);
                console.log(`[NPSPage] Competitor ${name} dataset prepared with topicId ${compTopicId}.`);
            } else {
                 console.log(`[NPSPage] Competitor ${name} dataset preparation returned null/undefined.`);
            }
        });

        // --- Create Final Chart Data Object ---
        const finalChartData = {
            labels: comprehensiveLabels, // Use the generated comprehensive labels
            datasets: allDatasets
        };

        console.log('[NPSPage] Final chart data object:', { labels: comprehensiveLabels.length > 0 ? `[${comprehensiveLabels[0]}...${comprehensiveLabels[comprehensiveLabels.length-1]}] (${comprehensiveLabels.length})` : '[]', datasets: finalChartData.datasets.map(d=>d.label) });
        // Debug: Log topicIds in final chart data
        console.log('[NPSPage] Datasets with topicIds:', finalChartData.datasets.map(d => ({ label: d.label, topicId: d.topicId })));
        setFilteredChartData(finalChartData);

    }, [npsResults, timeRange, showConfidenceIntervals, selectedProductName, showCompetitors, selectedProduct, topicMap]);

    // useEffect to set product name when topicMap or selection changes
    useEffect(() => {
        if (topicMap && Object.keys(topicMap).length > 0) {
            if (selectedProduct && topicMap[selectedProduct]) {
                // Get the product name for display
                const productName = topicMap[selectedProduct].name || selectedProduct;
                setSelectedProductName(productName);
            }
        }
    }, [topicMap, selectedProduct]);

    // Log when selectedProduct changes
    useEffect(() => {
        console.log('NPSPage - Selected product changed:', {
            selectedProduct,
            productName: topicMap?.[selectedProduct]?.name,
            priority: topicMap?.[selectedProduct]?.priority
        });
    }, [selectedProduct, topicMap]);

    const determineInitialTimeRange = (postsData) => {
        if (!postsData || postsData.length === 0) return '3y';

        // Get the main company's data (first dataset)
        const mainCompanyData = Object.values(competitorData)[0] || [];
        if (mainCompanyData.length === 0) return '3y';

        const now = new Date();
        const earliestPost = Math.min(...mainCompanyData.map(post => post.time));
        const earliestDate = new Date(earliestPost * 1000);
        const yearsDiff = (now - earliestDate) / (1000 * 60 * 60 * 24 * 365);

        // Select the appropriate time range that would include all data
        if (yearsDiff <= 1) return '1y';
        if (yearsDiff <= 3) return '3y';
        if (yearsDiff <= 5) return '5y';
        if (yearsDiff <= 10) return '10y';
        return 'all';
    };

    const handleClosePostsReferenced = () => {
        setShowPostDisplay(false);
        setPostsReferenced([]);
    };

    const handleTimeRangeChange = useCallback((newRange) => {
        console.log('Time range changed to:', newRange);
        setTimeRange(newRange);
    }, []);

    const getNPSColorClass = (nps) => {
        if (nps >= 30) return 'positive';
        if (nps <= 0) return 'negative';
        return 'neutral';
    };

    // Handler for chart point click - fetches posts for the clicked month
    const handleChartPointClick = useCallback(async ({ label, value, pointData, datasetIndex }) => {
        console.log('Chart point clicked!', { label, value, pointData, datasetIndex });
        console.log('Available datasets:', filteredChartData?.datasets?.length);

        // Debug: Log all datasets and their topicIds
        filteredChartData?.datasets?.forEach((ds, i) => {
            console.log(`Dataset ${i}: ${ds.label}, topicId: ${ds.topicId}`);
        });

        if (!label) return;

        // Get the topicId from the clicked dataset (could be main product or competitor)
        const clickedDataset = filteredChartData?.datasets?.[datasetIndex];
        console.log('Clicked dataset object:', clickedDataset);
        console.log('Clicked dataset topicId:', clickedDataset?.topicId);

        const topicId = clickedDataset?.topicId || selectedProduct;
        const datasetLabel = clickedDataset?.label || 'Unknown';

        console.log(`Clicked on dataset: ${datasetLabel}, using topicId: ${topicId}`);

        if (!topicId) return;

        try {
            // Parse the month (YYYY-MM) into start and end dates
            const monthDate = parse(label, 'yyyy-MM', new Date());
            const startDate = startOfMonth(monthDate);
            const endDate = endOfMonth(monthDate);

            const periodLabel = format(monthDate, 'MMMM yyyy');
            setSelectedTimePeriod({ label: `${datasetLabel} - Posts from ${periodLabel}` });

            console.log(`Fetching NPS posts for ${datasetLabel} in ${periodLabel}...`);

            // Fetch posts using the same endpoint as sentiment analysis
            const response = await apiPost('topic/sentiment/posts', {
                topic_id: topicId,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                type: 'NPS',
                source_types: null
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const posts = result.data || result || [];

            console.log(`Found ${posts.length} posts for ${periodLabel}`);

            setPostsReferenced(posts);
            setShowPostDisplay(true);
        } catch (error) {
            console.error('Error fetching posts for chart click:', error);
        }
    }, [selectedProduct, filteredChartData]);

    // Handler for metric card click - fetches posts for the time period
    const handleMetricCardClick = useCallback(async (timePeriod) => {
        if (!selectedProduct) return;

        try {
            const now = new Date();
            let startDate;
            let periodLabel;

            // Determine the time period based on the card clicked
            if (timePeriod === 'year') {
                startDate = subYears(now, 1);
                periodLabel = 'Past Year';
            } else if (timePeriod === 'threeYears') {
                startDate = subYears(now, 3);
                periodLabel = 'Past 3 Years';
            } else if (timePeriod === 'fiveYears') {
                startDate = subYears(now, 5);
                periodLabel = 'Past 5 Years';
            } else {
                console.error('Unknown time period:', timePeriod);
                return;
            }

            setSelectedTimePeriod({ label: `Posts from ${periodLabel}` });

            console.log(`Fetching NPS posts for ${periodLabel}...`);

            // Fetch posts using the same endpoint as sentiment analysis
            const response = await apiPost('topic/sentiment/posts', {
                topic_id: selectedProduct,
                start_date: startDate.toISOString(),
                end_date: now.toISOString(),
                type: 'NPS',
                source_types: null
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const posts = result.data || result || [];

            console.log(`Found ${posts.length} posts for ${periodLabel}`);

            setPostsReferenced(posts);
            setShowPostDisplay(true);
        } catch (error) {
            console.error('Error fetching posts for metric card click:', error);
        }
    }, [selectedProduct]);

    // Stock overlay (publicly-traded companies only). NPS uses monthly
    // 'YYYY-MM' labels, so align one value per month onto a y1 axis. 'price'
    // shows the absolute close; 'peers'/'snp500'/'sector' show the premium %.
    // NOTE: must run before any early return to satisfy the rules of hooks.
    const priceMode = overlayMode === 'price';
    const premiumMode = ['peers', 'snp500', 'sector'].includes(overlayMode) ? overlayMode : null;
    // timeRange is already a period string ('1y'/'2y'/'3y'/'5y'/'10y'); the
    // premium endpoint has no '2y', so fold it into '3y'.
    const premiumPeriod = timeRange === '2y' ? '3y' : timeRange;

    const { series: stockSeries } = useStockSeries(priceMode ? companyTicker : null);
    const { payload: premium, error: premiumError } = useStockPremium(companyTicker, premiumMode, premiumPeriod);

    const showStockOverlay = Boolean(companyTicker) && priceMode;
    const showPremiumOverlay = Boolean(companyTicker) && Boolean(premiumMode) && Boolean(premium?.data?.length);
    const benchLabel = premium ? benchmarkLabel(premium) : '';

    const overlayDataset = {
        borderColor: STOCK_COLOR,
        backgroundColor: STOCK_COLOR,
        borderDash: STOCK_DASH_ARRAY,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 0,
        yAxisID: 'y1',
        spanGaps: true,
        tension: 0.3,
        isStock: true,
    };
    const chartDataWithStock = useMemo(() => {
        if (!filteredChartData?.labels?.length) return filteredChartData;
        if (showStockOverlay) {
            const prices = pricesForMonthLabels(stockSeries, filteredChartData.labels);
            if (prices.every((p) => p == null)) return filteredChartData;
            const stockDataset = { ...overlayDataset, label: `Stock Price (${companyTicker})`, data: prices };
            return { ...filteredChartData, datasets: [...filteredChartData.datasets, stockDataset] };
        }
        if (showPremiumOverlay) {
            const prem = premiumForMonthLabels(premium.data, filteredChartData.labels);
            if (prem.every((p) => p == null)) return filteredChartData;
            const premiumDataset = { ...overlayDataset, label: `${companyTicker} vs ${benchLabel}`, data: prem };
            return { ...filteredChartData, datasets: [...filteredChartData.datasets, premiumDataset] };
        }
        return filteredChartData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showStockOverlay, showPremiumOverlay, filteredChartData, stockSeries, premium, companyTicker, benchLabel]);

    // Show loading spinner while waiting for priority 1 topic to load
    if (!topicMap || !priority1Loaded) {
        return (
            <div className="nps-page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress size={40} />
            </div>
        );
    }

    return (
        <div className="nps-page-container">
            <div className="controls-section">
                <div className="product-control">
                    <ProductSelector
                        topicMap={filteredTopicMap}
                        selectedId={selectedProduct || ''}
                        onSelect={selectProduct}
                    />
                    {true && (
                        <Toggle
                            label="Show Competitors"
                            checked={showCompetitors}
                            onChange={setShowCompetitors}
                        />
                    )}
                </div>
                
                <div className="view-controls">

                    <Toggle
                        label="Show Credible Intervals"
                        checked={showConfidenceIntervals}
                        onChange={setShowConfidenceIntervals}
                    />
                    {companyTicker && (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                                value={overlayMode}
                                onChange={(e) => setOverlayMode(e.target.value)}
                            >
                                <MenuItem value="off">No Overlay</MenuItem>
                                {PREMIUM_MODES.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>
                                        {m.value === 'price' ? `Price (${companyTicker})` : m.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {premiumMode && premiumError && (
                        <span style={{ fontSize: '12px', color: '#b00' }}>No comparison available</span>
                    )}
                    <TimeRangeSelector
                        timeRange={timeRange}
                        onTimeRangeChange={handleTimeRangeChange}
                    />
                </div>
            </div>

            <div className="chart-section">
                <NPSChart
                    chartData={chartDataWithStock}
                    showConfidenceIntervals={showConfidenceIntervals}
                    overlayKind={showStockOverlay ? 'price' : showPremiumOverlay ? 'premium' : null}
                    onPointClick={handleChartPointClick}
                />
            </div>
            
            <div className="metrics-section">
                <div className="metric-cards">
                    <MetricCard 
                        period="Past Year"
                        metrics={averages?.year || {}}
                        onClick={() => handleMetricCardClick('year')}
                        getNPSColorClass={getNPSColorClass}
                    />
                    <MetricCard 
                        period="Past 3 Years"
                        metrics={averages?.threeYears || {}}
                        onClick={() => handleMetricCardClick('threeYears')}
                        getNPSColorClass={getNPSColorClass}
                    />
                    <MetricCard 
                        period="Past 5 Years"
                        metrics={averages?.fiveYears || {}}
                        onClick={() => handleMetricCardClick('fiveYears')}
                        getNPSColorClass={getNPSColorClass}
                    />
                </div>
            </div>
            
            {showPostDisplay && postsReferenced.length > 0 && (
                <FilteredPostsDisplay 
                    posts={postsReferenced}
                    onClose={handleClosePostsReferenced}
                    id="nps-posts"
                    title={selectedTimePeriod?.label || "Referenced Posts"}
                    ratingDisplayName={stat_name}
                    autoScroll={true}
                    ratingColorScheme={{ high: 7, low: 4 }}
                    query={getAssessmentKeywords('nps')}
                />
            )}

        </div>
    );
};

export default NPSPage;